import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { SessionManager } from '../utils/sessionManager';

// Rotating conic-gradient glow behind the bubble (matches edlio/frontend
// AttentionBubble). @property enables animating the gradient angle; injected
// inline so the widget stays self-contained.
const GLOW_CSS = `
  @property --border-angle {
    syntax: "<angle>";
    inherits: false;
    initial-value: 0deg;
  }
  @keyframes border-rotate {
    to { --border-angle: 360deg; }
  }
`;
const GLOW_GRADIENT =
  'conic-gradient(from var(--border-angle), #3b82f6, #8b5cf6, #ec4899, #ef4444, #3b82f6)';
const GLOW_ANIMATION = 'border-rotate 1.5s linear infinite';

/**
 * AttentionBubble Component
 *
 * Displays a dismissible attention bubble above the chatbot FAB button to prompt user interaction.
 *
 * Visibility Rules:
 * - Shows on first page load if bubbleText is provided
 * - Hides after dismissal (session-persistent) - either by close button click or modal open
 * - Never shows if bubbleText is empty
 *
 * Design (matches edlio/frontend AttentionBubble):
 * - White pill bubble with a rotating conic-gradient glow and shadow
 * - Outline close button stacked above the bubble
 * - Fade-in / float animation
 * - No Escape key dismissal (click-only per requirements)
 *
 * @example
 * ```tsx
 * <AttentionBubble
 *   bubbleText="Hi! How can I help you?"
 *   position="right"
 * >
 *   <Button>Open Chatbot</Button>
 * </AttentionBubble>
 * ```
 */

const BUBBLE_STYLES = {
  maxWidth: 280,
} as const;

const ANIMATION_CONFIG = {
  /** Delay before triggering animation class (allows DOM to be ready) */
  triggerDelay: 50,
  /** Spacing between bubble and FAB button when animation is enabled */
  sideOffsetAnimated: 8,
  /** Spacing between bubble and FAB button when animation is disabled */
  sideOffsetStatic: 16,
} as const;

interface AttentionBubbleProps {
  /** Text to display in the bubble. If empty, bubble won't render. */
  bubbleText: string;
  /** Position of the FAB button - used to align bubble correctly */
  position?: 'left' | 'right';
  /** Whether to enable animation */
  animationEnabled?: boolean;
  /** Optional callback when bubble is dismissed */
  onDismiss?: () => void;
  /** The FAB button trigger element */
  children: ReactNode;
  onContentClick?: () => void;
}

export function AttentionBubble({
  bubbleText,
  position = 'right',
  animationEnabled = true,
  onDismiss,
  children,
  onContentClick,
}: AttentionBubbleProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  // Check session state and update visibility
  const checkVisibility = useCallback(() => {
    // Don't show if no text provided
    if (!bubbleText) {
      setIsVisible(false);
      return;
    }

    // Don't show if bubble was dismissed (either by close button or modal open)
    if (SessionManager.isBubbleDismissed()) {
      setIsVisible(false);
      return;
    }

    // All conditions met - show bubble
    setIsVisible(true);
  }, [bubbleText]);

  // Check visibility on mount and when bubbleText changes
  useEffect(() => {
    checkVisibility();
  }, [checkVisibility]);

  // Trigger animation after component mounts
  useEffect(() => {
    if (isVisible && animationEnabled) {
      // Small delay to ensure DOM is ready for animation
      const timer = setTimeout(
        () => setShouldAnimate(true),
        ANIMATION_CONFIG.triggerDelay,
      );
      return () => clearTimeout(timer);
    } else {
      setShouldAnimate(false);
    }
  }, [isVisible, animationEnabled]);

  // Listen for storage changes and custom events to react to dismissal in real-time
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Check if the dismissal key was updated (fired by other tabs)
      if (e.key === 'public-websearch-chatbot-bubble-dismissed') {
        checkVisibility();
      }
    };

    // Listen for custom events from same tab (dispatched by modal open handler)
    const handleCustomDismiss = () => {
      checkVisibility();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('chatbot-bubble-dismissed', handleCustomDismiss);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(
        'chatbot-bubble-dismissed',
        handleCustomDismiss,
      );
    };
  }, [checkVisibility]);

  const handleDismiss = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      SessionManager.dismissBubble();
      // Visibility will be updated by the event listener, but set immediately for instant feedback
      setIsVisible(false);
      onDismiss?.();
    },
    [onDismiss],
  );

  const handleContentClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      if (target.closest('button[aria-label="Close attention bubble"]')) {
        return;
      }

      if (onContentClick) {
        onContentClick();
      }
    },
    [onContentClick],
  );

  // Don't render anything if no bubble text
  if (!bubbleText) {
    return <>{children}</>;
  }

  // Don't render popover if not visible
  if (!isVisible) {
    return <>{children}</>;
  }

  // Calculate animation classes
  const animationClass = animationEnabled
    ? `attention-bubble-animated ${shouldAnimate ? 'visible' : ''}`
    : 'attention-bubble-no-animation';

  return (
    <Popover open={isVisible}>
      {/* Inline Animation Styles - Following PR #15949 pattern */}
      <style>{`
        ${GLOW_CSS}

        @keyframes bubbleEntry {
          from {
            opacity: 0;
            transform: translateY(100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bubbleFloat {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }

        .attention-bubble-animated {
          opacity: 0;
          transform: translateY(100%);
          will-change: opacity, transform;
        }

        .attention-bubble-animated.visible {
          animation:
            bubbleEntry 1s cubic-bezier(0.16, 1, 0.3, 1) 1s forwards,
            bubbleFloat 4s cubic-bezier(0.45, 0.05, 0.55, 0.95) 2s infinite;
        }

        .attention-bubble-no-animation {
          opacity: 1;
          transform: none;
          animation: none;
          will-change: auto;
        }

        @media (prefers-reduced-motion: reduce) {
          .attention-bubble-animated,
          .attention-bubble-animated.visible {
            animation: none !important;
            opacity: 1;
            transform: none;
            will-change: auto;
          }

          /* Stop the conic-gradient glow rotating (inline animation needs !important to override) */
          .attention-bubble-glow {
            animation: none !important;
          }
        }
      `}</style>

      <div className='relative size-full'>
        <PopoverTrigger asChild>
          <div className='pointer-events-none absolute inset-0' />
        </PopoverTrigger>
        {children}
      </div>
      <PopoverContent
        side='top'
        sideOffset={
          animationEnabled
            ? ANIMATION_CONFIG.sideOffsetAnimated
            : ANIMATION_CONFIG.sideOffsetStatic
        }
        align={position === 'left' ? 'start' : 'end'}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        avoidCollisions={false}
        collisionPadding={0}
        tabIndex={-1}
        role='note'
        className={`flex w-fit flex-col gap-1 border-none bg-transparent p-0 shadow-none ${
          position === 'left' ? 'items-start' : 'items-end'
        } ${animationClass}`}
        style={{ maxWidth: `${BUBBLE_STYLES.maxWidth}px`, zIndex: 1000 }}
        onClick={handleContentClick}
      >
        {/* Close Button - stacked above the bubble */}
        <button
          onClick={handleDismiss}
          tabIndex={-1}
          aria-label='Close attention bubble'
          className='flex h-8 w-8 items-center justify-center rounded-full border border-border bg-popover text-muted-foreground transition-transform hover:scale-110 active:scale-90 focus:outline-none focus:ring-1 focus:ring-gray-200 cursor-pointer'
        >
          <X className='h-4 w-4' strokeWidth={1.5} />
        </button>

        {/* Bubble with conic-gradient glow (rotates only when animation is enabled) */}
        <div className='relative isolate cursor-pointer'>
          <div
            aria-hidden='true'
            className='attention-bubble-glow pointer-events-none absolute -inset-0.5 rounded-[34px] opacity-50 blur-[4px]'
            style={{
              background: GLOW_GRADIENT,
              animation: animationEnabled ? GLOW_ANIMATION : 'none',
            }}
          />
          <div className='relative rounded-[32px] bg-popover px-6 py-6 shadow-lg'>
            <p className='m-0 whitespace-pre-line text-base font-medium leading-6 text-popover-foreground'>
              {bubbleText}
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
